import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

const MASKABLE_FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'name', label: 'Full Name' },
  { key: 'linkedinEmail', label: 'LinkedIn Email' },
  { key: 'phoneNumber', label: 'Phone Number' },
  { key: 'resumeKey', label: 'Resume Key' },
  { key: 'resumeContent', label: 'Resume Content' },
  { key: 'answerBankValues', label: 'Answer Bank Values' },
  { key: 'affectedUserIds', label: 'Affected User IDs' },
] as const;

export type MaskableFieldKey = (typeof MASKABLE_FIELDS)[number]['key'];

export interface RevealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (fields: MaskableFieldKey[], reason: string) => void;
}

interface FieldCheckboxGridProps {
  selected: Set<MaskableFieldKey>;
  onToggle: (key: MaskableFieldKey) => void;
}

function FieldCheckboxGrid({ selected, onToggle }: FieldCheckboxGridProps): React.ReactElement {
  return (
    <fieldset>
      <legend className="text-xs font-medium text-text-secondary mb-2">Fields to reveal</legend>
      <div className="grid grid-cols-2 gap-1.5">
        {MASKABLE_FIELDS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected.has(key)} onChange={() => onToggle(key)} />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

interface ReasonFieldProps {
  value: string;
  onChange: (v: string) => void;
}

function ReasonField({ value, onChange }: ReasonFieldProps): React.ReactElement {
  return (
    <div>
      <label htmlFor="reveal-reason" className="block text-xs font-medium text-text-secondary mb-1">
        Reason (required)
      </label>
      <textarea
        id="reveal-reason"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        required
        minLength={1}
        placeholder="e.g. investigating support ticket #1234"
        className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
      />
    </div>
  );
}

export function RevealModal({ open, onOpenChange, onSubmit }: RevealModalProps): React.ReactElement {
  const [selected, setSelected] = useState<Set<MaskableFieldKey>>(new Set());
  const [reason, setReason] = useState('');

  function toggleField(key: MaskableFieldKey): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!reason.trim() || selected.size === 0) return;
    onSubmit([...selected], reason.trim());
    setSelected(new Set());
    setReason('');
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <Dialog.Title className="text-lg font-semibold text-text-primary mb-1">Reveal PII Fields</Dialog.Title>
          <Dialog.Description className="text-sm text-text-secondary mb-4">
            Select the fields to unmask. All reveals are audit-logged.
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FieldCheckboxGrid selected={selected} onToggle={toggleField} />
            <ReasonField value={reason} onChange={setReason} />
            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <button type="button" className="px-4 py-2 text-sm text-text-secondary border border-border rounded-md hover:bg-surface-muted">
                  Cancel
                </button>
              </Dialog.Close>
              <button type="submit" disabled={selected.size === 0 || !reason.trim()}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50">
                Reveal
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
